// ---------------------------------------------------------------------------
// 콜드 스타트 낙관적 부팅 힌트. better-auth 클라이언트는 로컬 캐시가 없어
// useSession()이 콜드 스타트마다 무조건 pending으로 시작한다(GET /api/auth/
// get-session 왕복이 끝나야 확정) — 그 왕복이 끝나기 전에도 "최근에 로그인
// 상태였다"는 힌트로 RouterProvider를 먼저 마운트해 순백 화면을 없앤다.
// 실제 인가는 항상 서버 세션 쿠키가 하고, 이 힌트는 UI 낙관 렌더용일 뿐이다
// (세션이 무효로 확인되면 App.tsx의 리다이렉트 effect가 즉시 로그인으로 되돌린다).
// ---------------------------------------------------------------------------

const STORAGE_KEY = "withtask.auth-bootstrap";
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30일

export type AuthBootstrapHint = {
  userId: string;
  onboardingCompleted: boolean;
  savedAt: number;
};

function isAuthBootstrapHint(value: unknown): value is AuthBootstrapHint {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.userId === "string" &&
    typeof v.onboardingCompleted === "boolean" &&
    typeof v.savedAt === "number"
  );
}

/** 마지막으로 확인된 로그인 상태 힌트를 읽는다. 없거나, 형식이 깨졌거나,
 * 오래됐으면(MAX_AGE_MS 초과) null — 이 경우 호출부는 스켈레톤으로 폴백한다. */
export function readAuthBootstrap(): AuthBootstrapHint | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isAuthBootstrapHint(parsed)) return null;
    // 힌트가 너무 오래됐는지
    const isStale = Date.now() - parsed.savedAt > MAX_AGE_MS;
    return isStale ? null : parsed;
  } catch {
    // localStorage 접근 불가(사생활 보호 모드 등) 또는 JSON 파싱 실패
    return null;
  }
}

/** 서버 세션/온보딩 상태가 확정될 때마다 힌트를 갱신한다. */
export function saveAuthBootstrap(hint: Omit<AuthBootstrapHint, "savedAt">): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...hint, savedAt: Date.now() }));
  } catch {
    // 저장 실패해도 이번 세션 동작에는 영향 없음(다음 콜드 스타트에 스켈레톤으로 폴백)
  }
}

/** 로그아웃/세션 무효 확인 시 힌트를 지운다 — 안 지우면 다음 콜드 스타트가
 * 로그아웃된 계정으로 낙관 렌더했다가 로그인 화면으로 튕기는 깜빡임이 생긴다. */
export function clearAuthBootstrap(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // no-op
  }
}
